import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X } from "lucide-react";
import { Drawer } from "vaul";
import { AnimatePresence, motion } from "framer-motion";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import ChatPanel from "./ChatPanel";
import { useI18n } from "../lib/i18n";
import { renderError } from "../lib/errorMessage";
import {
  anonApi,
  buildAnonWsUrl,
  clearAnonChat,
  getAnonProfile,
  getAnonToken,
  startAnonChat,
} from "../lib/anonChat";

/**
 * Floating chat button + chat surface for visitor live chat.
 *
 * Adaptive:
 *  - Mobile / tablet (<lg): vaul Drawer slides up from the bottom edge.
 *    Sized via `dvh` so it adapts to the keyboard naturally.
 *  - Desktop (>=lg): a fixed-position rectangular popover anchored just
 *    above the FAB in the bottom-right corner — feels like a proper
 *    desktop chat widget rather than a full-width drawer.
 *
 * Hidden on /admin/*, /portal, and /book/confirmation/* where another
 * chat surface is already present.
 */
export default function ChatFab() {
  const { t, lang } = useI18n();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [hasSession, setHasSession] = useState(!!getAnonToken());
  const [profile, setProfile] = useState(getAnonProfile());
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState(profile?.name || "");
  const [contact, setContact] = useState(profile?.contact || "");
  const [error, setError] = useState(null);

  const [messages, setMessages] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [peerTyping, setPeerTyping] = useState(false);
  const [unread, setUnread] = useState(0);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const peerTypingTimerRef = useRef(null);
  const openRef = useRef(false);

  // Detect desktop (lg breakpoint = 1024px) so we can switch to a
  // fixed-position popover instead of the bottom drawer.
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(min-width: 1024px)").matches,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  // Hide on routes where another chat surface is already present.
  const HIDDEN_PREFIXES = ["/admin", "/portal", "/book/confirmation"];
  const hidden = HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));

  // Load history when chat opens with an existing session.
  useEffect(() => {
    if (!open || !hasSession) return;
    let alive = true;
    anonApi
      .get("/chat/anonymous/messages")
      .then((r) => {
        if (!alive) return;
        setMessages(r.data.messages || []);
        setUnread(0);
      })
      .catch((err) => {
        if (err.response?.status === 401 || err.response?.status === 404) {
          clearAnonChat();
          setHasSession(false);
          setProfile(null);
        }
      });
    return () => {
      alive = false;
    };
  }, [open, hasSession]);

  // WebSocket connection — kept open while session exists.
  useEffect(() => {
    if (!hasSession) return undefined;
    const token = getAnonToken();
    if (!token) return undefined;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      const ws = new WebSocket(buildAnonWsUrl(token));
      wsRef.current = ws;
      ws.onopen = () => setWsConnected(true);
      ws.onclose = () => {
        setWsConnected(false);
        if (!cancelled) reconnectRef.current = setTimeout(connect, 2500);
      };
      ws.onerror = () => {
        try { ws.close(); } catch (_) { /* ignore */ }
      };
      ws.onmessage = (e) => {
        try {
          const evt = JSON.parse(e.data);
          if (evt.type === "new_message" && evt.message) {
            setMessages((prev) =>
              prev.find((m) => m.id === evt.message.id)
                ? prev
                : [...prev, evt.message],
            );
            if (evt.message.sender === "admin" && !openRef.current) {
              setUnread((u) => u + 1);
            }
          } else if (evt.type === "typing" && evt.sender === "admin") {
            setPeerTyping(true);
            if (peerTypingTimerRef.current) clearTimeout(peerTypingTimerRef.current);
            peerTypingTimerRef.current = setTimeout(
              () => setPeerTyping(false),
              3000,
            );
          } else if (evt.type === "messages_read" && evt.reader === "admin") {
            setMessages((prev) =>
              prev.map((m) =>
                m.sender === "customer" && !m.read ? { ...m, read: true } : m,
              ),
            );
          }
        } catch (_) {
          /* ignore */
        }
      };
    };
    connect();
    return () => {
      cancelled = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (peerTypingTimerRef.current) clearTimeout(peerTypingTimerRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (_) { /* ignore */ }
      }
    };
  }, [hasSession]);

  const onStart = async (e) => {
    e?.preventDefault();
    setError(null);
    const trimmedName = name.trim();
    const trimmedContact = contact.trim();
    if (!trimmedName) {
      setError({ key: "chat_fab.error_name" });
      return;
    }
    if (!trimmedContact) {
      setError({ key: "chat_fab.error_contact" });
      return;
    }
    const isEmail =
      trimmedContact.includes("@") && trimmedContact.includes(".");
    const digits = trimmedContact.replace(/\D/g, "");
    if (!isEmail && digits.length < 7) {
      setError({ key: "chat_fab.error_contact" });
      return;
    }
    setSubmitting(true);
    try {
      const data = await startAnonChat({
        name: trimmedName,
        contact: trimmedContact,
        language: lang,
      });
      setHasSession(true);
      setProfile({
        name: trimmedName,
        contact: trimmedContact,
        conversation_id: data.conversation.id,
      });
    } catch (err) {
      setError({ key: "chat_fab.error_generic" });
    } finally {
      setSubmitting(false);
    }
  };

  const onSendMessage = async (body) => {
    try {
      const { data } = await anonApi.post("/chat/anonymous/messages", { body });
      setMessages((prev) => (prev.find((m) => m.id === data.id) ? prev : [...prev, data]));
    } catch (_) {
      toast.error(t("chat_fab.error_send"));
    }
  };

  const onTyping = () => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: "typing" }));
      } catch (_) { /* ignore */ }
    }
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) setUnread(0);
  };

  if (hidden) return null;

  // ---- Shared bits ------------------------------------------------------
  const headerBlock = (
    <div className="flex items-center justify-between px-5 pb-3 border-b border-slate-100">
      <div className="min-w-0">
        <div className="font-display text-lg font-bold text-slate-900 truncate">
          {t("chat_fab.title")}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {t("chat_fab.subtitle")}
        </div>
      </div>
      <button
        aria-label="Close chat"
        onClick={() => setOpen(false)}
        className="p-1.5 rounded-lg hover:bg-slate-100"
        data-testid="chat-fab-close"
      >
        <X className="w-5 h-5 text-slate-500" />
      </button>
    </div>
  );

  const startForm = (
    <form
      className="px-5 py-5 space-y-4 overflow-y-auto"
      onSubmit={onStart}
      data-testid="chat-fab-start-form"
    >
      <p className="text-sm text-slate-600 leading-snug">
        {t("chat_fab.intro")}
      </p>
      <div>
        <Label htmlFor="anon-name" className="text-xs">
          {t("chat_fab.name_label")}
        </Label>
        <Input
          id="anon-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("chat_fab.name_placeholder")}
          autoComplete="name"
          data-testid="chat-fab-name-input"
        />
      </div>
      <div>
        <Label htmlFor="anon-contact" className="text-xs">
          {t("chat_fab.contact_label")}
        </Label>
        <Input
          id="anon-contact"
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          placeholder={t("chat_fab.contact_placeholder")}
          autoComplete="email"
          inputMode="email"
          data-testid="chat-fab-contact-input"
        />
      </div>
      {error && (
        <p
          className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-1.5"
          data-testid="chat-fab-error"
        >
          {renderError(error, t)}
        </p>
      )}
      <button
        type="submit"
        className="w-full btn-primary inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
        disabled={submitting}
        data-testid="chat-fab-start-btn"
      >
        {submitting ? "…" : t("chat_fab.start_btn")}
      </button>
      <p className="text-[11px] text-slate-400 text-center pt-1">
        {t("chat_fab.disclaimer")}
      </p>
    </form>
  );

  const activeBlock = (
    <div
      className="flex-1 min-h-0 flex flex-col px-3 pt-2 pb-2"
      data-testid="chat-fab-active"
    >
      <ChatPanel
        fill
        messages={messages}
        onSend={onSendMessage}
        onTyping={onTyping}
        currentSender="customer"
        peerTyping={peerTyping}
        wsConnected={wsConnected}
        peerName={t("portal.chat_admin_label")}
        emptyKey="chat_fab.empty"
        placeholderKey="portal.chat_placeholder"
        sendKey="portal.chat_send"
        testidPrefix="chat-fab-chat"
        introKey="chat_fab.active_intro"
      />
    </div>
  );

  // ---- Trigger button ---------------------------------------------------
  const triggerButton = (
    <button
      type="button"
      aria-label={t("chat_fab.open_chat")}
      onClick={() => handleOpenChange(true)}
      className="fixed z-[95] bottom-[calc(4.75rem+env(safe-area-inset-bottom))] lg:bottom-6 right-4 lg:right-6 w-14 h-14 rounded-full bg-sky-600 text-white shadow-xl shadow-sky-900/30 grid place-items-center hover:bg-sky-700 active:scale-95 transition"
      data-testid="chat-fab-btn"
    >
      <MessageCircle className="w-6 h-6" />
      {unread > 0 && (
        <span
          className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-rose-500 text-[11px] font-bold grid place-items-center"
          data-testid="chat-fab-unread"
        >
          {unread}
        </span>
      )}
    </button>
  );

  // ---- Desktop popover (>= lg) -----------------------------------------
  if (isDesktop) {
    return (
      <>
        {triggerButton}
        <AnimatePresence>
          {open && (
            <motion.div
              key="desktop-chat-popover"
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.96 }}
              transition={{ duration: 0.18, ease: [0.32, 0.72, 0, 1] }}
              className="fixed z-[125] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
              style={{
                right: "1.5rem",
                bottom: "5.5rem", // above the FAB (24px + 56px)
                width: "min(380px, calc(100vw - 3rem))",
                height: "min(620px, calc(100vh - 7rem))",
              }}
              data-testid="chat-fab-popover"
              role="dialog"
              aria-label={t("chat_fab.title")}
            >
              {headerBlock}
              {!hasSession ? startForm : activeBlock}
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  // ---- Mobile / tablet drawer (< lg) -----------------------------------
  return (
    <Drawer.Root
      open={open}
      onOpenChange={handleOpenChange}
      // `repositionInputs` lets vaul automatically lift the drawer when
      // the on-screen keyboard appears (iOS Safari + Android Chrome) so
      // the focused input never ends up behind the keyboard. Without it
      // we get the dreaded phantom gap.
      repositionInputs
    >
      <Drawer.Trigger asChild>{triggerButton}</Drawer.Trigger>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/40 z-[120]" />
        <Drawer.Content
          // Cap at 88% of the *dynamic* viewport. vaul subtracts the
          // keyboard height itself when `repositionInputs` is on, so we
          // don't fight it with our own visualViewport math (which was
          // creating the gap above the keyboard).
          className="fixed bottom-0 left-0 right-0 z-[125] bg-white rounded-t-3xl shadow-2xl flex flex-col h-[88dvh] max-h-[88dvh]"
          data-testid="chat-fab-drawer"
        >
          <Drawer.Title className="sr-only">{t("chat_fab.title")}</Drawer.Title>
          <Drawer.Description className="sr-only">
            {t("chat_fab.intro")}
          </Drawer.Description>
          <div className="flex-shrink-0 pt-2.5 pb-1.5 grid place-items-center">
            <div className="w-10 h-1 rounded-full bg-slate-300" />
          </div>
          {headerBlock}
          {!hasSession ? startForm : activeBlock}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
